import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import FormSkeleton from './formSkeleton';
import StepNote from './stepNote';
import FormField from 'components/common/formikForm/formField';
import { FormBusyProvider } from 'components/common/formikForm/formBusyContext';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import { StepNavigation } from 'components/v2/StepNavigation';

import { BooleanValue } from 'constant';
import useEnabledCountries from 'hooks/useEnabledCountries';
import {
    useRegions,
    useProfessions,
    useNominationOptionsByCountry,
    useWorkPermits,
    useJobOffer,
    useSaveJobOffer,
} from 'hooks';
import useTranslate from 'hooks/useTranslate';
import { RootState } from 'redux/store';
import type { JobOffer } from 'types';
import { chosenCountryName, countryNameOptions, rowCountryEnabled } from 'utils/countries';
import { generateOptionFromObject, generateOptionsFromString } from 'utils/dataFormatter';
import { createJobOfferValidationSchema } from 'utils/validations/jobOfferSchema';

const booleanOptions = generateOptionFromObject<boolean>(BooleanValue);

/** Defaults used by AssessmentV2 JobOfferForm on country change. */
const DONT_KNOW = "Don't know";
const NOI_YES = 'I have an NOI';
const NOI_NO = "Don't have it";

/** Canada NOI yes/no — option values are the API nomination strings. */
const canadaNoiOptions = [
    { label: 'Yes', value: NOI_YES },
    { label: 'No', value: NOI_NO },
];

const emptyJobOffer: Partial<JobOffer> = {
    country: '',
    hourly_wage: 0,
    nomination: '',
    profession: '',
    region: '',
    work_permit: '',
    zip: '',
};

/**
 * Country-change defaults from JobOfferForm.handleUpdate('country').
 */
const applyCountryDefaults = (country: string): Partial<JobOffer> => {
    const isAustralia = country === 'Australia';
    const isUk = country === 'United Kingdom';
    const isCanada = country === 'Canada';
    const isAusOrUk = isAustralia || isUk;

    return {
        country,
        region: '',
        profession: '',
        zip: '',
        // Australia: free-select from nomination list (start empty)
        // Canada: NOI yes/no strings
        // UK: default "Don't know" until user picks from list
        nomination: isAustralia ? '' : isCanada ? NOI_NO : DONT_KNOW,
        // AU/UK: work_permit not shown — store "Don't know"
        // Canada: user selects (start empty; not required by old validate)
        work_permit: isAusOrUk ? DONT_KNOW : '',
    };
};

interface JobOfferStepProps {
    isLastStep: boolean;
    formData: Record<string, unknown>;
    onNext: (data: Record<string, unknown>) => void;
    onBack: () => void;
}

const JobOfferStep = ({ onNext, onBack, isLastStep }: JobOfferStepProps) => {
    const user = useSelector((state: RootState) => state.user.entity);
    const { codes } = useEnabledCountries();
    const validationSchema = useMemo(() => createJobOfferValidationSchema(codes), [codes]);

    const { data: jobOfferData, isLoading } = useJobOffer();
    const saveJobOffer = useSaveJobOffer();
    const { data: regions } = useRegions();
    const { data: workPermits } = useWorkPermits();

    const workPermitOptions = generateOptionsFromString(workPermits || []);

    const initialValues = useMemo(() => {
        const first = jobOfferData && jobOfferData.length > 0 ? jobOfferData[0] : null;
        // Normalize Canada NOI display if legacy true/false somehow stored
        let offer = first;
        if (offer && offer.country === 'Canada') {
            if (offer.nomination === 'I have an NOI' || offer.nomination === NOI_YES) {
                offer = { ...offer, nomination: NOI_YES };
            } else if (
                offer.nomination === "Don't have it" ||
                offer.nomination === DONT_KNOW ||
                !offer.nomination
            ) {
                // Keep as-is if already NOI_NO; otherwise leave for user
                offer = { ...offer };
            }
        }
        return {
            has_job_offer: Boolean(first),
            // Old form only ever edits a single offer (data[0])
            job_offers: offer ? [offer] : [],
        };
    }, [jobOfferData]);

    const handleSubmit = async (values: { has_job_offer?: boolean; job_offers: JobOffer[] }) => {
        try {
            const payload = values.has_job_offer
                ? (values.job_offers || []).map((item) => ({
                      ...item,
                      user_id: user?.id || 0,
                  }))
                : [];
            await saveJobOffer.mutateAsync(payload);
            onNext({ job_offers: payload });
        } catch {
            // Error alert is handled by the mutation hook onError
        }
    };

    if (isLoading) {
        return <FormSkeleton fields={8} />;
    }

    return (
        <Box>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, isValid, isSubmitting, setFieldValue, setValues }) => {
                    const offer = values.job_offers?.[0];
                    const country = offer?.country || '';
                    const countryOptions = generateOptionsFromString(
                        countryNameOptions(codes, country)
                    );
                    const isCanada = rowCountryEnabled(codes, country, 'CA');
                    const showNominationDropdown =
                        rowCountryEnabled(codes, country, 'AU') ||
                        rowCountryEnabled(codes, country, 'UK');

                    const regionOptions = generateOptionsFromString(
                        country ? (regions as Record<string, string[]> | null)?.[country] || [] : []
                    );

                    const isSaving = isSubmitting || saveJobOffer.isPending;

                    return (
                        <FormBusyProvider busy={isSaving}>
                            <Form>
                                <FormField
                                    row
                                    type="radio"
                                    name="has_job_offer"
                                    label="validJobOffer"
                                    options={booleanOptions}
                                    onChange={(value: unknown) => {
                                        const boolValue = value === true || value === 'true';
                                        const soleCountry = chosenCountryName(codes);
                                        // Update flag + list atomically so Yup.when('has_job_offer')
                                        // never validates an empty job_offers while flag is still true.
                                        // Return the promise so FormField waits before re-validating.
                                        return setValues(
                                            {
                                                ...values,
                                                has_job_offer: boolValue,
                                                job_offers: boolValue
                                                    ? [
                                                          {
                                                              ...emptyJobOffer,
                                                              ...(soleCountry
                                                                  ? applyCountryDefaults(
                                                                        soleCountry
                                                                    )
                                                                  : {}),
                                                          } as JobOffer,
                                                      ]
                                                    : [],
                                            },
                                            true
                                        );
                                    }}
                                />

                                {values.has_job_offer && offer && (
                                    <FieldArray name="job_offers">
                                        {() => (
                                            <Paper elevation={0} sx={{ mt: 2 }}>
                                                <FormGridLayout>
                                                    {/* Country + region (always when has offer) */}
                                                    <FormField
                                                        required
                                                        type="autosuggest"
                                                        name="job_offers.0.country"
                                                        label="country"
                                                        options={countryOptions}
                                                        onChange={(value) => {
                                                            const nextCountry = String(value || '');
                                                            const defaults =
                                                                applyCountryDefaults(nextCountry);
                                                            // Apply all country-change defaults atomically
                                                            Object.entries(defaults).forEach(
                                                                ([key, val]) => {
                                                                    setFieldValue(
                                                                        `job_offers.0.${key}`,
                                                                        val
                                                                    );
                                                                }
                                                            );
                                                        }}
                                                    />
                                                    <FormField
                                                        required
                                                        type="autosuggest"
                                                        name="job_offers.0.region"
                                                        label="region"
                                                        options={regionOptions}
                                                        disabled={!country}
                                                    />
                                                    {rowCountryEnabled(codes, country, 'AU') && (
                                                        <FormField
                                                            name="job_offers.0.zip"
                                                            label="zipLabel"
                                                            tooltip="zipTooltipJobOffer"
                                                        />
                                                    )}

                                                    {/* Canada only: work permit + NOI yes/no */}
                                                    {isCanada && (
                                                        <>
                                                            <FormField
                                                                required
                                                                type="autosuggest"
                                                                name="job_offers.0.work_permit"
                                                                label="workPermit"
                                                                options={workPermitOptions}
                                                            />
                                                            <FormField
                                                                required
                                                                type="radio"
                                                                row
                                                                name="job_offers.0.nomination"
                                                                label="doYouHaveNOI"
                                                                options={canadaNoiOptions}
                                                            />
                                                        </>
                                                    )}

                                                    {/* Australia / UK: employer nomination list + tooltip */}
                                                    {showNominationDropdown && (
                                                        <NominationField
                                                            index={0}
                                                            country={country}
                                                        />
                                                    )}

                                                    <FormField
                                                        required
                                                        name="job_offers.0.hourly_wage"
                                                        label="hourlyWageLocalCurrency"
                                                        type="number"
                                                    />
                                                </FormGridLayout>

                                                <Box sx={{ mt: { xs: 2, sm: 2.5 } }}>
                                                    <ProfessionNote />
                                                    <FormGridLayout>
                                                        <ProfessionField
                                                            index={0}
                                                            country={country}
                                                        />
                                                    </FormGridLayout>
                                                </Box>
                                            </Paper>
                                        )}
                                    </FieldArray>
                                )}

                                <StepNavigation
                                    isLastStep={isLastStep}
                                    onBack={onBack}
                                    isSubmitting={isSaving}
                                    disableNext={!isValid}
                                />
                            </Form>
                        </FormBusyProvider>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default JobOfferStep;

const ProfessionNote = () => {
    const { translate } = useTranslate();
    return (
        <StepNote fullWidth>
            <>
                <Box component="span" sx={{ display: 'block', mb: 0.5 }}>
                    {translate('thisSectionIsImportant')}
                </Box>
                <Box component="span" sx={{ display: 'block' }}>
                    {translate('exactProfession')}
                </Box>
            </>
        </StepNote>
    );
};

const ProfessionField = ({ index, country }: { index: number; country: string }) => {
    const { data, isFetching } = useProfessions(country || '');
    const professionOptions = generateOptionsFromString(data || []);

    return (
        <FormField
            required
            type="autosuggest"
            name={`job_offers.${index}.profession`}
            label="profession"
            options={professionOptions}
            disabled={!country || isFetching}
        />
    );
};

/**
 * Employer nomination for Australia / UK with EOI/NOI tooltip
 * (AssessmentV2 IconTooltipTextField).
 */
const NominationField = ({ index, country }: { index: number; country: string }) => {
    const { translate } = useTranslate();
    const { data: nominations, isFetching } = useNominationOptionsByCountry(country);
    const nominationOptions = generateOptionsFromString(nominations || []);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.75,
                width: '100%',
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormField
                    required
                    type="autosuggest"
                    name={`job_offers.${index}.nomination`}
                    label="employerNomination"
                    options={nominationOptions}
                    disabled={!country || isFetching}
                />
            </Box>
            <Tooltip
                arrow
                placement="top"
                title={
                    <Box sx={{ p: 0.5, maxWidth: 260 }}>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}
                        >
                            {translate('govtOrPrivateInstitution')}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
                            <strong>EOI</strong> – Expression Of Interest
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                            <strong>NOI</strong> – Notification Of Interest
                        </Typography>
                    </Box>
                }
            >
                <InfoOutlinedIcon
                    fontSize="small"
                    color="action"
                    sx={{ mt: { xs: 1.25, sm: 1.5 }, cursor: 'help', flexShrink: 0 }}
                />
            </Tooltip>
        </Box>
    );
};
